import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Check, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Premium() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [livePlans, setLivePlans] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/plans"); setLivePlans(data); }
      catch {}
    })();
  }, []);

  const PERKS = [t("prem.perks.0"), t("prem.perks.1"), t("prem.perks.2"), t("prem.perks.3"), t("prem.perks.4"), t("prem.perks.5")];
  const symbol = (c) => c === "inr" ? "₹" : "$";
  const fmt = (n) => Number(n).toLocaleString("en-IN");
  const PLANS = (livePlans && livePlans.length === 2)
    ? [
        { id: "monthly", label: t("prem.monthly"), price: `${symbol(livePlans.find(p=>p.id==='monthly')?.currency || 'inr')}${fmt(livePlans.find(p=>p.id==='monthly')?.amount)}`, per: "/mo", best: false },
        { id: "annual",  label: t("prem.annual"),  price: `${symbol(livePlans.find(p=>p.id==='annual')?.currency || 'inr')}${fmt(livePlans.find(p=>p.id==='annual')?.amount)}`, per: "/yr", best: true },
      ]
    : [
        { id: "monthly", label: t("prem.monthly"), price: "₹799", per: "/mo", best: false },
        { id: "annual",  label: t("prem.annual"),  price: "₹6,499", per: "/yr", best: true },
      ];

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { plan, origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const subscribeRazorpay = async (plan) => {
    setBusy("rzp-" + plan);
    try {
      const { data } = await api.post("/payments/razorpay/order", { plan });
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: data.name,
        description: data.description,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: "#C85A44" },
        handler: async (response) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful! Premium activated.");
            window.location.href = "/premium/success?razorpay=1";
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => setBusy(null) },
      };
      if (!window.Razorpay) {
        toast.error("Payment SDK not loaded — please refresh");
        setBusy(null);
        return;
      }
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed: " + (resp.error?.description || ""));
        setBusy(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="premium-page">
      <div className="text-center max-w-2xl mx-auto">
        <Crown className="h-7 w-7 text-[#C85A44] mx-auto mb-4" strokeWidth={1.5} />
        <p className="overline mb-3">{t("prem.kicker")}</p>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight leading-tight">
          {t("prem.title")}
        </h1>
        <p className="mt-5 text-[#5d574f]">
          {t("prem.subtitle")}
        </p>
      </div>

      {user?.is_premium && (
        <div className="mt-10 mx-auto max-w-xl rounded-2xl bg-[#F2EFE9] border border-[#E2DCD0] p-6 text-center" data-testid="already-premium">
          <Sparkles className="h-5 w-5 text-[#C85A44] mx-auto mb-2" strokeWidth={1.5} />
          <p className="font-serif-display text-xl">{t("prem.already")}</p>
          <p className="text-sm text-[#8C857B] mt-1">{t("prem.plan")}: {user.premium_plan}</p>
        </div>
      )}

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {PLANS.map((p) => (
          <div key={p.id} data-testid={`plan-${p.id}`}
            className={`p-8 md:p-10 rounded-3xl border ${p.best ? "border-[#C85A44] bg-white relative" : "border-[#E2DCD0] bg-white"}`}>
            {p.best && (
              <span className="absolute -top-3 left-8 bg-[#C85A44] text-white text-xs uppercase tracking-widest rounded-full px-3 py-1">
                {t("prem.recommended")} · {t("prem.save34")}
              </span>
            )}
            <p className="overline mb-2">{p.label}</p>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-serif-display text-5xl">{p.price}</span>
              <span className="text-sm text-[#8C857B]">{p.per}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {PERKS.map((perk) => (
                <li key={perk} className="flex items-start gap-3 text-sm">
                  <Check className="h-4 w-4 text-[#2E5C46] mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => subscribe(p.id)} disabled={busy === p.id || user?.is_premium}
              data-testid={`subscribe-${p.id}`}
              className={`w-full rounded-full text-sm font-medium px-6 py-3 transition ${
                p.best
                  ? "bg-[#C85A44] hover:bg-[#A84A36] text-white"
                  : "bg-[#2C2A28] hover:bg-black text-white"
              } disabled:opacity-60`}>
              {busy === p.id ? t("prem.redirecting") : user?.is_premium ? t("prem.youArePremium") : `${t("prem.choose")} ${p.label}`}
            </button>
            <button onClick={() => subscribeRazorpay(p.id)} disabled={busy === ("rzp-" + p.id) || user?.is_premium}
              data-testid={`subscribe-rzp-${p.id}`}
              className={`mt-3 w-full rounded-full text-sm font-medium px-6 py-3 transition border ${
                p.best
                  ? "border-[#C85A44] text-[#C85A44] hover:bg-[#FAE5E0]"
                  : "border-[#2C2A28] text-[#2C2A28] hover:bg-[#F2EFE9]"
              } disabled:opacity-60 inline-flex items-center justify-center gap-2`}>
              {busy === ("rzp-" + p.id) ? t("prem.redirecting") : <>Pay with UPI / Card<span className="text-xs opacity-70">· Razorpay</span></>}
            </button>
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-[#8C857B] mt-8">{t("prem.secureBy")}</p>
    </div>
  );
}
